import { supabase } from './supabase'
import { OnboardingState, Goal } from '../onboarding/types'
import { User } from '@supabase/supabase-js'
import { resolveDeterministicPersonId } from './goalCapability'

export interface PersistenceResult {
  profileId: string
  householdId: string
  ownerMemberId?: string
  childPersonId?: string
  personIdByRelationship?: Record<string, string>
  goalsCount: number
}

export async function saveOnboardingToSupabase(
  user: User,
  state: OnboardingState
): Promise<PersistenceResult> {
  // Step 1: Check existing profile to preserve custom display_name
  const { data: existingProfile } = await supabase
    .from('profiles')
    .select('id, display_name, preferred_language, default_currency')
    .eq('auth_user_id', user.id)
    .maybeSingle()

  const fallbackDisplayName =
    user.user_metadata?.display_name ||
    user.email?.split('@')[0] ||
    'Pengguna BekalNanti'

  // Preserve existing non-empty profile display_name
  const effectiveDisplayName =
    existingProfile?.display_name && existingProfile.display_name.trim().length > 0
      ? existingProfile.display_name
      : fallbackDisplayName

  let profile = existingProfile
  if (!existingProfile) {
    const { data: newProfile, error: profErr } = await supabase
      .from('profiles')
      .insert({
        auth_user_id: user.id,
        display_name: effectiveDisplayName,
        preferred_language: 'id',
        default_currency: 'IDR',
      })
      .select()
      .single()

    if (profErr) {
      throw new Error(`Gagal menyimpan profil: ${profErr.message}`)
    }
    profile = newProfile
  } else if (!existingProfile.display_name || existingProfile.display_name.trim().length === 0) {
    const { data: updatedProfile, error: profUpdateErr } = await supabase
      .from('profiles')
      .update({ display_name: effectiveDisplayName })
      .eq('id', existingProfile.id)
      .select()
      .single()
    if (!profUpdateErr && updatedProfile) {
      profile = updatedProfile
    }
  }

  if (!profile) {
    throw new Error('Gagal memproses profil pengguna')
  }

  const displayName = effectiveDisplayName

  // Step 2: Household (Find existing created by profile or create new)
  let { data: household, error: houseQueryErr } = await supabase
    .from('households')
    .select()
    .eq('created_by_profile_id', profile.id)
    .maybeSingle()

  if (houseQueryErr) {
    throw new Error(`Gagal memeriksa rumah tangga: ${houseQueryErr.message}`)
  }

  if (!household) {
    const { data: newHousehold, error: houseInsertErr } = await supabase
      .from('households')
      .insert({
        created_by_profile_id: profile.id,
        name: `Keluarga ${displayName}`,
        country: 'ID',
        base_currency: 'IDR',
      })
      .select()
      .single()

    if (houseInsertErr) {
      throw new Error(`Gagal membuat rumah tangga: ${houseInsertErr.message}`)
    }
    household = newHousehold
  }

  // Step 3: Household Members (Owner record)
  let { data: member, error: memQueryErr } = await supabase
    .from('household_members')
    .select()
    .eq('household_id', household.id)
    .eq('profile_id', profile.id)
    .maybeSingle()

  if (memQueryErr) {
    throw new Error(`Gagal memeriksa keanggotaan: ${memQueryErr.message}`)
  }

  if (!member) {
    const { data: newMember, error: memInsertErr } = await supabase
      .from('household_members')
      .insert({
        household_id: household.id,
        profile_id: profile.id,
        role: 'owner',
      })
      .select()
      .single()

    if (memInsertErr) {
      throw new Error(`Gagal mendaftarkan pemilik rumah tangga: ${memInsertErr.message}`)
    }
    member = newMember
  }

  // Step 4: Persons Reconciliation (NON-DESTRUCTIVE)
  const { data: existingPersons, error: persQueryErr } = await supabase
    .from('persons')
    .select('id, relationship, full_name, status')
    .eq('household_id', household.id)

  if (persQueryErr) {
    throw new Error(`Gagal memeriksa data keluarga: ${persQueryErr.message}`)
  }

  interface ContextPersonSpec {
    field: keyof OnboardingState['planningContext']
    relationship: string
    getDefaultName: (dn: string) => string
  }

  const CONTEXT_SPECS: ContextPersonSpec[] = [
    { field: 'self', relationship: 'self', getDefaultName: (dn) => dn },
    { field: 'spousePartner', relationship: 'spouse', getDefaultName: () => 'Pasangan' },
    { field: 'child', relationship: 'child', getDefaultName: () => 'Anak Pertama' },
    { field: 'family', relationship: 'family', getDefaultName: () => 'Keluarga' },
    { field: 'otherDependent', relationship: 'dependent', getDefaultName: () => 'Tanggungan' },
    { field: 'multiplePeople', relationship: 'household', getDefaultName: () => 'Anggota Lain' },
  ]

  let childPersonId: string | undefined = undefined
  const personIdByRelationship: Record<string, string> = {}

  for (const spec of CONTEXT_SPECS) {
    const isSelected = Boolean(state.planningContext[spec.field])
    const matched = (existingPersons || []).filter((p: any) => p.relationship === spec.relationship)

    if (isSelected) {
      if (matched.length === 0) {
        const { data: newPerson, error: insErr } = await supabase
          .from('persons')
          .insert({
            household_id: household.id,
            full_name: spec.getDefaultName(displayName),
            relationship: spec.relationship,
            status: 'active',
          })
          .select()
          .single()

        if (insErr) {
          throw new Error(`Gagal menyimpan data konteks ${spec.field}: ${insErr.message}`)
        }
        personIdByRelationship[spec.relationship] = newPerson.id
        if (spec.field === 'child') {
          childPersonId = newPerson.id
        }
      } else if (matched[0]?.id) {
        // If the existing person was archived, explicitly selecting it reactivates it
        if (matched[0].status === 'archived') {
          await supabase
            .from('persons')
            .update({ status: 'active', updated_at: new Date().toISOString() })
            .eq('id', matched[0].id)
        }
        personIdByRelationship[spec.relationship] = matched[0].id
        if (spec.field === 'child') {
          childPersonId = matched[0].id
        }
      }
    } else {
      // NON-DESTRUCTIVE: Unchecking in onboarding must NEVER delete persons or their goals!
      // Existing persons remain in the household and People Management.
      // We deliberately do NOT put them in personIdByRelationship so newly added goals
      // are not attributed to unchecked relationships.
    }
  }

  // Step 5: Goals Reconciliation
  const { data: existingGoals, error: goalsQueryErr } = await supabase
    .from('goals')
    .select('id, goal_type, name, status, person_id, priority')
    .eq('household_id', household.id)

  if (goalsQueryErr) {
    throw new Error(`Gagal memeriksa tujuan: ${goalsQueryErr.message}`)
  }

  const selectedTypes = new Set(state.selectedGoals.map((g) => g.type))
  const existingMap = new Map<string, any>()
  for (const g of existingGoals || []) {
    existingMap.set(g.goal_type, g)
  }

  // 1. Reconcile existing goals
  for (const existing of existingGoals || []) {
    if (selectedTypes.has(existing.goal_type)) {
      // Determine what person_id this goal should have
      const targetPersonId = resolveDeterministicPersonId(existing.goal_type, personIdByRelationship)

      // Goal is selected: check if reactivation or person reassignment is required
      const shouldReactivate = existing.status === 'archived'
      const isMisassignedToChild =
        existing.goal_type !== 'education' &&
        childPersonId &&
        existing.person_id === childPersonId &&
        personIdByRelationship['self'] &&
        targetPersonId !== childPersonId

      const shouldUpdatePerson =
        isMisassignedToChild ||
        (existing.person_id === null && targetPersonId !== null)

      if (shouldReactivate || shouldUpdatePerson) {
        const updates: any = {
          updated_at: new Date().toISOString(),
        }
        if (shouldReactivate) {
          updates.status = 'draft'
        }
        if (shouldUpdatePerson) {
          updates.person_id = targetPersonId
        }

        const { error: updateErr } = await supabase
          .from('goals')
          .update(updates)
          .eq('id', existing.id)

        if (updateErr) {
          throw new Error(`Gagal memperbarui tujuan ${existing.name}: ${updateErr.message}`)
        }
      }
    } else {
      // Goal was DESELECTED: mark as archived if currently active
      if (existing.status !== 'archived') {
        const { error: archiveErr } = await supabase
          .from('goals')
          .update({
            status: 'archived',
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id)

        if (archiveErr) {
          throw new Error(`Gagal menonaktifkan tujuan ${existing.name}: ${archiveErr.message}`)
        }
      }
    }
  }

  // 2. Insert new goals that don't exist yet with proper deterministic person_id
  for (const g of state.selectedGoals) {
    if (!existingMap.has(g.type)) {
      const targetPersonId = resolveDeterministicPersonId(g.type, personIdByRelationship)
      const { error: goalErr } = await supabase.from('goals').insert({
        household_id: household.id,
        person_id: targetPersonId,
        name: g.title,
        goal_type: g.type,
        status: 'draft',
        priority: 'medium',
      })
      if (goalErr) {
        throw new Error(`Gagal menambahkan tujuan ${g.title}: ${goalErr.message}`)
      }
    }
  }

  return {
    profileId: profile.id,
    householdId: household.id,
    ownerMemberId: member?.id,
    childPersonId,
    personIdByRelationship,
    goalsCount: state.selectedGoals.length,
  }
}

export async function loadOnboardingFromSupabase(
  user: User,
  goalCatalog: Goal[]
): Promise<{ state: OnboardingState; householdId: string } | null> {
  // Step 1: Find profile
  const { data: profile } = await supabase
    .from('profiles')
    .select()
    .eq('auth_user_id', user.id)
    .maybeSingle()

  if (!profile) return null

  // Step 2: Find household
  const { data: household } = await supabase
    .from('households')
    .select()
    .eq('created_by_profile_id', profile.id)
    .maybeSingle()

  if (!household) return null

  // Step 3: Find active persons only (archived persons must not re-check planning context)
  const { data: persons, error: persErr } = await supabase
    .from('persons')
    .select('id, relationship, full_name, status')
    .eq('household_id', household.id)
    .eq('status', 'active')

  if (persErr) {
    console.warn('Error fetching persons:', persErr)
  }

  const activePersons = (persons || []).filter((p: any) => p.status !== 'archived')
  const personRelationships = new Set(activePersons.map((p: any) => p.relationship))

  const planningContext = {
    self: personRelationships.has('self'),
    child: personRelationships.has('child'),
    spousePartner: personRelationships.has('spouse'),
    family: personRelationships.has('family'),
    otherDependent: personRelationships.has('dependent'),
    multiplePeople: personRelationships.has('household'),
  }

  // Step 4: Find active goals only
  const { data: goals, error: goalsErr } = await supabase
    .from('goals')
    .select()
    .eq('household_id', household.id)
    .neq('status', 'archived')

  if (goalsErr) {
    console.warn('Error fetching active goals:', goalsErr)
  }

  const selectedGoals: Goal[] = []
  for (const g of goals || []) {
    const matched = goalCatalog.find((item) => item.type === g.goal_type)
    if (matched) {
      selectedGoals.push(matched)
    } else {
      selectedGoals.push({
        id: g.goal_type,
        title: g.name,
        emoji: '',
        type: g.goal_type,
      })
    }
  }

  return {
    state: {
      entryMode: 'goalFirst',
      planningContext,
      selectedGoals,
    },
    householdId: household.id,
  }
}
